import type { MouseEventHandler } from "react";
import { cn } from "@/lib/utils";
import { ExternalLink } from "@/components/ui/external-link";
import { Icon } from "@/components/ui/icon"
import {
  buildAppleMapsUrl,
  buildGoogleMapsUrl,
  buildWazeUrl,
  openPreferredMaps,
  type MapsLocation,
} from "@/utils/maps.utils";

type OpenMapsActionsProps = {
  address: string;
  lat?: number | null;
  lng?: number | null;
  className?: string;
  /** Para cards clicáveis — evita abrir o card ao tocar nos links. */
  stopPropagation?: boolean;
  variant?: "inline" | "buttons";
};

function wrapClick(
  handler: () => void,
  stopPropagation?: boolean,
): MouseEventHandler {
  return (event) => {
    if (stopPropagation) {
      event.stopPropagation();
    }
    handler();
  };
}

const MAP_BUTTON_BASE =
  "flex items-center justify-center gap-0.5 rounded-lg h-8 px-1 text-[8px] sm:text-[9px] font-semibold leading-none whitespace-nowrap border active:scale-95 transition-all min-w-0 overflow-hidden";

const MAP_ICON_CLASS = "text-[12px] shrink-0";

const MAP_BUTTON_STYLES = {
  google:
    "bg-[#4285F4] border-[#4285F4] text-white hover:bg-[#3367D6] hover:border-[#3367D6]",
  waze: "bg-[#33CCFF] border-[#33CCFF] text-[#001A33] hover:bg-[#2BB8E8] hover:border-[#2BB8E8]",
  apple:
    "bg-neutral-900 border-neutral-900 text-white hover:bg-neutral-800 hover:border-neutral-800 dark:bg-neutral-100 dark:border-neutral-100 dark:text-neutral-900 dark:hover:bg-white dark:hover:border-white",
} as const;

export function OpenMapsActions({
  address,
  lat,
  lng,
  className,
  stopPropagation = false,
  variant = "inline",
}: OpenMapsActionsProps) {
  const location: MapsLocation = { address, lat, lng };
  const linkClass =
    "inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline";

  if (variant === "buttons") {
    return (
      <div
        className={cn("grid grid-cols-3 gap-1.5", className)}
        onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}
      >
        <ExternalLink
          href={buildGoogleMapsUrl(location)}
          className={cn(MAP_BUTTON_BASE, MAP_BUTTON_STYLES.google)}
          title="Google Maps"
          onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}
        >
          <Icon name="map" className={MAP_ICON_CLASS} />
          Google Maps
        </ExternalLink>
        <ExternalLink
          href={buildWazeUrl(location)}
          className={cn(MAP_BUTTON_BASE, MAP_BUTTON_STYLES.waze)}
          title="Waze"
          onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}
        >
          <Icon name="navigation" className={MAP_ICON_CLASS} />
          Waze
        </ExternalLink>
        <ExternalLink
          href={buildAppleMapsUrl(location)}
          className={cn(MAP_BUTTON_BASE, MAP_BUTTON_STYLES.apple)}
          title="Apple Maps"
          onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}
        >
          <Icon name="map" className={MAP_ICON_CLASS} />
          Apple Maps
        </ExternalLink>
      </div>
    );
  }

  return (
    <div
      className={cn("flex flex-wrap items-center gap-x-2 gap-y-1", className)}
      onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}
    >
      <button
        type="button"
        onClick={wrapClick(() => openPreferredMaps(location), stopPropagation)}
        className={cn(linkClass, "text-sm")}
      >
        <Icon name="directions" />
        Como chegar
      </button>
      <span className="text-muted-foreground/60 text-xs" aria-hidden>
        ·
      </span>
      <ExternalLink
        href={buildGoogleMapsUrl(location)}
        className={linkClass}
        onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}
      >
        Google Maps
      </ExternalLink>
      <span className="text-muted-foreground/60 text-xs" aria-hidden>
        ·
      </span>
      <ExternalLink
        href={buildWazeUrl(location)}
        className={linkClass}
        onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}
      >
        Waze
      </ExternalLink>
      <span className="text-muted-foreground/60 text-xs" aria-hidden>
        ·
      </span>
      <ExternalLink
        href={buildAppleMapsUrl(location)}
        className={linkClass}
        onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}
      >
        Apple Maps
      </ExternalLink>
    </div>
  );
}
